import {
  type AgentTargetDetail,
  AgentTargetDetailSchema,
  type AgentTargetListQuery,
  type AgentTargetListResponse,
  AgentTargetListResponseSchema,
  type AgentTargetStatsQuery,
  type AgentTargetStatsResponse,
  AgentTargetStatsResponseSchema,
  type AssignableAgent,
  AssignableAgentListSchema,
  type CreateAgentTargetRequest,
  type CurrencyOption,
  CurrencyOptionListSchema,
  type UpdateAgentTargetRequest,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * Agent Target master API + option lookups. Every response is validated with
 * the shared Zod schema so a server change can't sneak past the UI types.
 */

export const listAgentTargets = async (
  query: AgentTargetListQuery,
): Promise<AgentTargetListResponse> => {
  const { data } = await apiClient.get<unknown>('/agent-targets', { params: query });
  return AgentTargetListResponseSchema.parse(data);
};

export const getAgentTarget = async (id: string): Promise<AgentTargetDetail> => {
  const { data } = await apiClient.get<unknown>(`/agent-targets/${id}`);
  return AgentTargetDetailSchema.parse(data);
};

export const createAgentTarget = async (
  payload: CreateAgentTargetRequest,
): Promise<AgentTargetDetail> => {
  const { data } = await apiClient.post<unknown>('/agent-targets', payload);
  return AgentTargetDetailSchema.parse(data);
};

export const updateAgentTarget = async (
  id: string,
  payload: UpdateAgentTargetRequest,
): Promise<AgentTargetDetail> => {
  const { data } = await apiClient.patch<unknown>(`/agent-targets/${id}`, payload);
  return AgentTargetDetailSchema.parse(data);
};

export const deleteAgentTarget = async (id: string): Promise<void> => {
  await apiClient.delete(`/agent-targets/${id}`);
};

export const bulkDeleteAgentTargets = async (
  ids: string[],
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/agent-targets/bulk-delete', { ids });
  return data;
};

/**
 * Pipeline strip stats. Returns my + team buckets pre-aggregated and
 * RBAC-scoped server-side, so the Lead Inbox tile strip doesn't have to
 * page through every target / WON lead. See ADR-0043.
 */
export const getAgentTargetStats = async (
  query: AgentTargetStatsQuery,
): Promise<AgentTargetStatsResponse> => {
  const { data } = await apiClient.get<unknown>('/agent-targets/stats', { params: query });
  return AgentTargetStatsResponseSchema.parse(data);
};

// ----- Option lookups (slim dropdowns) -----

/** Slim agent list for the userId picker.
 *  Sourced from `GET /users/assignable-agents`, which is hierarchy-scoped
 *  server-side: ADMIN sees every active SALES_LEAD + SALES_AGENT, a
 *  SALES_LEAD sees themselves + their direct reports, and a SALES_AGENT
 *  sees only themselves. The endpoint also widens the role gate so a
 *  sales user can populate the picker even though /users itself is
 *  admin-only.
 *  `branchId` is surfaced so consumers (e.g. the leads form) can auto-
 *  fill the lead's branch when an agent is picked. */
export const listAgentOptions = async (): Promise<
  Array<{ id: string; fullName: string; branchId: string | null; role: AssignableAgent['role'] }>
> => {
  const { data } = await apiClient.get<unknown>('/users/assignable-agents');
  const parsed = AssignableAgentListSchema.parse(data);
  // 2026-07-08. Keep `role` on the option row so callers can render
  // role-grouped dropdowns (e.g. the lead inbox owner filter groups by
  // SALES_LEAD vs SALES_AGENT). Existing consumers ignore the extra key.
  return parsed.map((u) => ({
    id: u.id,
    fullName: u.fullName,
    branchId: u.branchId,
    role: u.role,
  }));
};

/** Currency option list for the currencyId picker (active only). */
export const listCurrencyOptions = async (): Promise<CurrencyOption[]> => {
  const { data } = await apiClient.get<unknown>('/currencies/options');
  return CurrencyOptionListSchema.parse(data);
};
